import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BarChart3, AlertTriangle, ArrowRight } from 'lucide-react';
import { TopSection } from '../../ui/TopSection';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { StatCard } from '../../ui/StatCard';
import { metricIcon } from '../../ui/metricIcons';
import InfoHelper from '../../common/InfoHelper';
import { RestaurantFilterDropdown } from '../../ui/RestaurantFilterDropdown';
import { MenuCostVsActualChart } from '../charts/MenuCostVsActualChart';
import { MenuEngineeringChart } from '../charts/MenuEngineeringChart';
import { CostPercentWasteCard } from './CostPercentWasteCard';
import { QuadrantTopCards } from './QuadrantTopCards';
import { useCostPercentData } from './useCostPercentData';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { currencyFormatter } from '../../../services/helperService';
import { iconBgPurple, iconBgAmber, cardClass, secondaryText, semiBoldText, largeTextSize } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';

const CostPercentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const d = useCostPercentData();

  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Keep the overall-type filter to options this page supports. Depends on the
  // stable filter value + setter (NOT the whole hook object, which is a fresh
  // reference each render and would re-run this effect every render).
  const { overallTypeFilter, setOverallTypeFilter } = d;
  useEffect(() => {
    if (overallTypeFilter && !['all', 'food', 'drinks'].includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  const availableRestaurants = d.isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [];

  // Inefficiency tile: either the € value (pp subtitle + tooltip) or a
  // "set up cost price" prompt when too little of the menu is costed.
  const inefficiencyCard = !d.hasEnoughCostSetup ? (
    <div className={cn(cardClass, 'p-5 flex flex-col h-full')}>
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center w-[42px] h-[42px] rounded-[8px] flex-shrink-0" style={{ backgroundColor: iconBgAmber }}>
          <AlertTriangle className="w-6 h-6" style={{ color: '#F59E0B' }} />
        </span>
        <span className={cn(secondaryText, largeTextSize, semiBoldText)}>{t('costPercent.kpi.inefficiency')}</span>
      </div>
      <p className={cn(secondaryText, 'text-[14px] mt-4')}>{t('costPercent.kpi.setupNeeded')}</p>
      <button
        onClick={() => navigate('/costprice')}
        className="mt-3 flex items-center gap-1 text-[14px] font-semibold text-textGreen dark:text-textGreen-dark hover:underline self-start"
      >
        {t('costPercent.kpi.setupCta')}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  ) : (
    <StatCard
      icon={<AlertTriangle className="w-6 h-6" style={{ color: '#F59E0B' }} />}
      iconBg={iconBgAmber}
      title={t('costPercent.kpi.inefficiency')}
      value={d.inefficiencyEuro !== null ? currencyFormatter.format(d.inefficiencyEuro) : '--'}
      subtitle={
        <span className="flex items-center gap-1">
          {d.inefficiencyPp !== null
            ? t('costPercent.kpi.inefficiencyPp', { value: d.inefficiencyPp.toFixed(1) })
            : ''}
          <InfoHelper titleKey="costPercent.kpi.inefficiency" descriptionKey="costPercent.kpi.inefficiencyTooltip" />
        </span>
      }
    />
  );

  return (
    <div>
      <MobilePageHeader title={t('costPercent.title')} right={<RestaurantSelector />} />

      <TopSection
        title={t('costPercent.title')}
        backButton={false}
        dateRange={d.dateRange}
        setDateRange={d.setDateRange}
        selectedTimeOption={d.selectedTimeOption as any}
        setSelectedTimeOption={d.setSelectedTimeOption as any}
        overallTypeOptions={['all', 'food', 'drinks']}
        onOverallTypeChange={(v) => d.setOverallTypeFilter(v as 'all' | 'food' | 'drinks')}
        currentOverallTypeValue={d.overallTypeFilter}
        rightControls={d.isAllRestaurants ? (
          <RestaurantFilterDropdown
            restaurants={availableRestaurants}
            selectedRestaurantIds={restaurantFilters}
            setSelectedRestaurantIds={setRestaurantFilters}
          />
        ) : undefined}
      />

      <div className="mt-6 space-y-6">
        {/* KPI row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            {...metricIcon('costPercent')}
            title={t('costPercent.kpi.costPercent')}
            value={d.costPercentage}
            valueType="percentage"
            changeValue={d.costPercentageChange?.changeValue}
            changeLabel={d.costPercentageChange?.changeLabel}
            trendDirection="down"
            loading={d.loading}
          />
          <StatCard
            icon={<BarChart3 className="w-6 h-6" style={{ color: '#6938EF' }} />}
            iconBg={iconBgPurple}
            title={t('costPercent.kpi.avgCostPercent')}
            value={d.avgCostPercentage}
            valueType="percentage"
            loading={d.loading}
          />
          {inefficiencyCard}
        </div>

        {/* Actual vs theoretical trend with the dotted target line */}
        <MenuCostVsActualChart
          data={d.menuCostVsActualData}
          isAllRestaurants={d.isAllRestaurants}
          overallTypeFilter={d.overallTypeFilter}
          selectedTimeOption={d.selectedTimeOption as any}
          loading={d.loading}
          hideKpis
          allowAllRestaurants
          targetLine={d.targetPct}
        />

        {/* Waste + menu engineering */}
        {d.isAllRestaurants ? (
          <CostPercentWasteCard side={d.overallTypeFilter} />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <MenuEngineeringChart
                  data={d.menuEngineeringData.data}
                  hasEnoughData={d.menuEngineeringData.hasEnoughData}
                  loading={d.loading}
                />
              </div>
              <CostPercentWasteCard side={d.overallTypeFilter} />
            </div>
            <QuadrantTopCards data={d.menuEngineeringData.data} />
          </>
        )}
      </div>
    </div>
  );
};

export default CostPercentPage;
